import * as React from "react";
import {
  LineChart,
  DEFAULT_X_AXIS_KEY,
  DEFAULT_Y_AXIS_KEY,
  AxisItemIdentifier,
} from "@mui/x-charts";
import { MultipleLineChartProps } from "./types";
import { TooltipFooterContext } from "./tooltipFooterContext";
import { TooltipWithFooter } from "./TooltipWithFooter";
import { formatCompactNumber } from "../../../utils";
import { useResponsive } from "../../../hooks/useResponsive";
import { calculateTickInterval, getResponsiveFontSize } from "../utils";
import { BREAKPOINTS } from "../../../utils/constants";

// Exported so the legend swatch in `PerformanceChart/styles.ts` cannot drift from the line.
export const DASH_DOT_PATTERN = "8 4 1 4";

const highlightedAxisOf = (
  index: number | null | undefined,
): AxisItemIdentifier[] | undefined => {
  if (index === undefined) {
    return undefined;
  }
  return index === null
    ? []
    : [{ axisId: DEFAULT_X_AXIS_KEY, dataIndex: index }];
};

export const MultipleLineChartComponent: React.FC<MultipleLineChartProps> = ({
  series,
  height,
  xLabels,
  yMax,
  yMin,
  width,
  yLabel,
  xLabel,
  hideLegend = false,
  hideYAxis = false,
  hideXAxisLine = false,
  hideYAxisLine = false,
  showHorizontalGrid = false,
  tooltipFooter,
  yTicks,
  highlightedIndex,
  onHighlightedIndexChange,
  hideTooltip = false,
}) => {
  const isMobile = useResponsive(BREAKPOINTS.mobile);

  const resolvedSeries = series.map((s, index) => ({
    ...s,
    id: s.id ?? `series-${index}`,
    type: "line" as const,
  }));

  const lineStyleSx = resolvedSeries.reduce<Record<string, object>>(
    (acc, s) => {
      if (s.dashed) {
        acc[`& .MuiLineChart-line[data-series-id="${s.id}"]`] = {
          strokeDasharray: "5 6",
        };
      }
      if (s.dotted) {
        acc[`& .MuiLineChart-line[data-series-id="${s.id}"]`] = {
          strokeDasharray: "1 6",
          strokeLinecap: "round",
        };
      }
      if (s.dashDot) {
        acc[`& .MuiLineChart-line[data-series-id="${s.id}"]`] = {
          strokeDasharray: DASH_DOT_PATTERN,
        };
      }
      return acc;
    },
    {},
  );

  const highlightedAxis = highlightedAxisOf(highlightedIndex);

  // An empty list means the pointer left the chart; the last inspected point stays.
  const handleHighlightedAxisChange = onHighlightedIndexChange
    ? (axisItems: AxisItemIdentifier[]) => {
        const dataIndex = axisItems[0]?.dataIndex;
        if (dataIndex !== undefined) {
          onHighlightedIndexChange(dataIndex);
        }
      }
    : undefined;

  let leftMargin = 20;
  if (hideYAxis) {
    leftMargin = 8;
  } else if (isMobile) {
    leftMargin = 15;
  }

  return (
    <TooltipFooterContext.Provider value={tooltipFooter}>
      <div style={{ width: "100%", height, minHeight: height }}>
        <LineChart
          aria-label={yLabel || xLabel || "Multiple line chart"}
          series={resolvedSeries}
          height={height}
          width={width}
          hideLegend={hideLegend}
          grid={{ horizontal: showHorizontalGrid }}
          highlightedAxis={highlightedAxis}
          onHighlightedAxisChange={handleHighlightedAxisChange}
          margin={
            isMobile
              ? {
                  left: leftMargin,
                  right: 10,
                  top: 20,
                  bottom: hideLegend ? 50 : 100,
                }
              : {
                  left: leftMargin,
                  right: 20,
                  top: 20,
                  bottom: hideLegend ? 30 : 60,
                }
          }
          xAxis={[
            {
              scaleType: "point",
              data: xLabels,
              id: DEFAULT_X_AXIS_KEY,
              label: xLabel,
              disableLine: hideXAxisLine,
              disableTicks: hideXAxisLine,
              tickInterval: (value: any, index: number) => {
                const step = calculateTickInterval(xLabels.length, isMobile);
                return index % step === 0;
              },
              tickLabelStyle: {
                angle: 0,
                textAnchor: "middle",
                fontFamily: "'Crimson Pro', sans-serif",
                fontSize: getResponsiveFontSize(isMobile, 12),
                fill: "var(--color-text-default) !important",
              },
              labelStyle: {
                fontFamily: "'Crimson Pro', sans-serif",
                fontSize: 12,
                fill: "var(--color-text-default) !important",
              },
            },
          ]}
          yAxis={[
            {
              id: DEFAULT_Y_AXIS_KEY,
              min: yMin as number | undefined,
              max: yMax as number | undefined,
              scaleType: "linear",
              tickInterval: yTicks,
              label: hideYAxis ? undefined : yLabel,
              width: hideYAxis ? 0 : undefined,
              disableLine: hideYAxis || hideYAxisLine,
              disableTicks: hideYAxis || hideYAxisLine,
              valueFormatter: (value: number) => formatCompactNumber(value),
              tickLabelStyle: hideYAxis
                ? { display: "none" }
                : {
                    angle: 0,
                    textAnchor: "end",
                    fontFamily: "'Crimson Pro', sans-serif",
                    fontSize: getResponsiveFontSize(isMobile, 12),
                    fill: "var(--color-text-default) !important",
                  },
              labelStyle: {
                fontFamily: "'Crimson Pro', sans-serif",
                fontSize: getResponsiveFontSize(isMobile, 12),
                fill: "var(--color-text-default) !important",
              },
            },
          ]}
          sx={{
            ...lineStyleSx,
            "& .MuiChartsAxis-tickLabel": {
              fontFamily: "'Crimson Pro', sans-serif",
              fill: "var(--color-text-default) !important",
              fontSize: `${getResponsiveFontSize(isMobile, 12)}px`,
            },
            "& .MuiChartsAxis-label": {
              fontFamily: "'Crimson Pro', sans-serif",
              fontWeight: "bold",
              fill: "var(--color-text-default) !important",
              fontSize: `${getResponsiveFontSize(isMobile, 12)}px`,
            },
            "& .MuiChartsAxis-tick": {
              strokeWidth: isMobile ? 1 : 1.5,
              stroke: "var(--color-text-default) !important",
            },
            "& .MuiChartsAxis-line": {
              stroke: "var(--color-text-default) !important",
              strokeWidth: isMobile ? 1 : 1.5,
            },
            "& .MuiChartsLegend-label": {
              fontFamily: "'Crimson Pro', sans-serif",
              color: "var(--color-text-default)",
              fontSize: isMobile ? "var(--text-base)" : "var(--text-lg)",
            },
            "& .MuiLineChart-area": {
              fillOpacity: 0.18,
            },
            "& .MuiChartsGrid-line": {
              stroke: "var(--color-border) !important",
              strokeOpacity: 0.5,
            },
            overflow: "visible",
          }}
          slots={tooltipFooter ? { tooltip: TooltipWithFooter } : undefined}
          slotProps={{
            tooltip: {
              trigger: hideTooltip ? "none" : undefined,
              sx: {
                minWidth: "fit-content",
                whiteSpace: "nowrap",
                "& .MuiChartsTooltip-table": {
                  width: "100%",
                },
                "& .MuiChartsTooltip-cell": {
                  padding: "8px 12px",
                },
              },
            },
          }}
        />
      </div>
    </TooltipFooterContext.Provider>
  );
};
